"use client";

import Link from "next/link";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, useState } from "react";
import { NavBtn } from "../navButton/NavBtn";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./style.css";

interface FeaturedPost {
  id: string;
  title: string;
  summary: string;
  author: string;
  author_username: string;
  date: string;
  title_slug: string;
  url: string;
  image: string;
  category_name?: string;
  category_slug?: string;
  category_color?: string;
}

interface Props {
  featuredPosts: FeaturedPost[];
}

export default function FeaturedSliderSmall({ featuredPosts }: Props) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  if (!featuredPosts?.length) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    return new Date(dateString).toLocaleString("en-US", {
      month: "short", // Feb
      day: "2-digit",
      year: "numeric", // 2026
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // AM/PM format
    });
  };

  return (
    <section className="featured-slider small-slider px-0 px-md-3">
      <div className="featured-slider__container">
        <div className="slider-wrapper" style={{ position: "relative" }}>
          <Swiper
            modules={[Autoplay, Navigation]}
            slidesPerView={1}
            loop
            autoplay={{ delay: 3000 }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onSwiper={(swiper) => {
              // @ts-ignore
              swiper.params.navigation.prevEl = prevRef.current;
              // @ts-ignore
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
            observer={true}
            observeParents={true}
            resizeObserver={true}
          >
            {featuredPosts.map((post) => (
              <SwiperSlide key={post.id}>
                <div className="featured-card small-card">
                  <Image
                    src={post.image}
                    alt={post.title}
                    fill
                    priority
                    fetchPriority="high"
                    className="featured-card__img"
                    sizes="(max-width: 768px) 100vw, 430px"
                  />

                  <div className="featured-card__content">
                    <Link href={`/${post.url || post.title_slug}`}>
                      <span className="featured-card__date px-3">
                        {post.author} • {formatDate(post.date)}
                      </span>
                      <h2 className="featured-card__title_small px-0 px-md-2">
                        {post.title}
                      </h2>
                    </Link>

                    <Link
                      href={`/${post.category_slug || "#"}`}
                      className="featured-card__category px-4"
                    >
                      {post.category_name}
                    </Link>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <NavBtn
            ref={prevRef}
            direction="prev"
            hover={hoverPrev}
            className="small-prev-btn"
            onMouseEnter={() => setHoverPrev(true)}
            onMouseLeave={() => setHoverPrev(false)}
          />
          <NavBtn
            ref={nextRef}
            direction="next"
            hover={hoverNext}
            className="small-next-btn"
            onMouseEnter={() => setHoverNext(true)}
            onMouseLeave={() => setHoverNext(false)}
          />
        </div>
      </div>
    </section>
  );
}
